import { useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import { Button, GeneralFormError, AlertModalLayout, ControlledInput } from '@ui-components/neo.tax';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { DeleteCompanyMutation, DeleteCompanyMutationVariables, DELETE_COMPANY } from '@neo.tax/shared/graphql';
import { GetCompaniesQueryCompany } from '@neo.tax/shared/hooks/useCompanies';
import { useAnalyticId } from '@neo.tax/shared/state';

import { DeleteCompanyFormValues } from './types';

interface DeleteCompanyProps {
  close: () => void;
  company: GetCompaniesQueryCompany | undefined;
}

export const DeleteCompany = ({ close, company }: DeleteCompanyProps): React.ReactElement | null => {
  const companyNameInputId = useAnalyticId(AnalyticIdentifier.RTC_DELETE_COMPANY_NAME_INPUT);
  const [errorMessage, setErrorMessage] = useState<string>();

  const { control, formState, handleSubmit, reset } = useForm<DeleteCompanyFormValues>({
    mode: 'onChange',
    defaultValues: {
      shortName: '',
    },
  });

  const [deleteCompany, { loading }] = useMutation<DeleteCompanyMutation, DeleteCompanyMutationVariables>(
    DELETE_COMPANY,
  );

  const onSubmit = async (): Promise<void> => {
    if (!company?.id) return;
    try {
      await deleteCompany({
        variables: { companyId: company.id },
        update: (cache) => {
          cache.modify({
            id: cache.identify(company),
            fields: (_, { DELETE }) => DELETE,
          });
        },
      });
      close();
    } catch {
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const onClickCancel = (): void => {
    close();
    reset();
  };

  if (!company) return null;
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AlertModalLayout
        title={`Delete company "${company.shortName}"`}
        copy={`Are you sure you want to delete ${company.shortName}? The credit and all company data will be permanently removed.`}
        trailingContent={
          <>
            <Button size="base" variant="white" rounded="regular" onClick={onClickCancel}>
              Cancel
            </Button>
            <Button
              disabled={!formState.isValid || loading}
              rounded="regular"
              size="base"
              type="submit"
              variant="rsbry"
            >
              {loading ? 'Deleting company...' : 'Delete'}
            </Button>
          </>
        }
      >
        <ControlledInput
          control={control}
          name="shortName"
          rules={{ required: true, validate: (shortName) => shortName === company.shortName }}
          id={companyNameInputId}
          label="To delete, please type the company name:"
        />
        <GeneralFormError centered error={!!errorMessage}>
          {errorMessage}
        </GeneralFormError>
      </AlertModalLayout>
    </form>
  );
};
